import { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';

import { Button, type ColumnDef, DataTable, dialogs, Pagination, SELECTED_ALL_ID, Spinner, Text } from '@nvision/ui';

import type { Pricing } from '@/modules/pricing';

import { useContractPricing, useDeletePricing } from '../../../hooks';
import { type ContractPricingItem, type ContractPricingParams, contractPricingParamsSchema } from '../../../models';
import { PricingSearchModal } from './pricing-search-modal';

import styles from './contract-pricing.module.css';

export type ContractPricingRef = {
  linkedPricing: Pricing[];
  reset: () => void;
};

interface ContractPricingProps {
  contractKey?: string;
  pricingRef: React.RefObject<ContractPricingRef>;
}

const EMPTY_VALUE = '—';

const columns: ColumnDef<ContractPricingItem>[] = [
  {
    accessorKey: 'pricingKey',
    header: 'Pricing Number',
    size: 140,
  },
  {
    cell: (props) => props.row.original.carrier?.carrierScacCode || EMPTY_VALUE,
    header: 'SCAC',
    id: 'scac',
    size: 100,
  },
  {
    accessorKey: 'carrier',
    cell: (props) => props.row.original.carrier?.carrierName || props.row.original.carrier?.label || EMPTY_VALUE,
    header: 'Transportation Provider',
    size: 180,
  },
  {
    accessorKey: 'branch',
    cell: (props) => props.row.original.branch?.label || EMPTY_VALUE,
    header: 'Branch',
    size: 100,
  },
  {
    accessorKey: 'service',
    cell: (props) => props.row.original.service?.label || EMPTY_VALUE,
    header: 'Service',
    size: 140,
  },
  {
    accessorKey: 'cargoType',
    cell: (props) => props.row.original.cargoType?.label || EMPTY_VALUE,
    header: 'Cargo Type',
    size: 120,
  },
  {
    accessorKey: 'pricingBasis',
    cell: (props) => props.row.original.pricingBasis?.label || EMPTY_VALUE,
    header: 'Pricing Basis',
    size: 160,
  },
  {
    accessorKey: 'currency',
    cell: (props) => props.row.original.currency?.label || EMPTY_VALUE,
    header: 'Currency',
    size: 120,
  },
  {
    accessorKey: 'pricingType',
    cell: (props) => props.row.original.pricingType?.label || EMPTY_VALUE,
    header: 'Pricing Type',
    size: 180,
  },
];

export const ContractPricing: React.FC<ContractPricingProps> = ({ contractKey, pricingRef }) => {
  const { t } = useTranslation(['translation']);
  const [isPricingSearchOpen, setIsPricingSearchOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [linkedPricing, setLinkedPricing] = useState<Pricing[]>([]);
  const [params, setParams] = useState<ContractPricingParams>({
    ...v.getDefaults(contractPricingParamsSchema),
    contractKey: contractKey ?? '',
  });
  const [requestParams, setRequestParams] = useState<ContractPricingParams>(params);

  const { data: pricing, isLoading } = useContractPricing(requestParams);
  const { isPending: isDeleting, mutate: deletePricing } = useDeletePricing();

  const existingCount = pricing?.totalCount ?? 0;
  const totalCount = existingCount + linkedPricing.length;
  const page = params.offset / params.limit + 1;
  const items = [
    ...(params.offset < existingCount ? (pricing?.items ?? []) : []),
    ...linkedPricing.slice(
      Math.max(0, params.offset - existingCount),
      Math.max(0, params.offset + params.limit - existingCount),
    ),
  ];

  const changeParams = (nextParams: ContractPricingParams, nextExistingCount = existingCount) => {
    setParams(nextParams);

    if (nextParams.offset < nextExistingCount) {
      setRequestParams(nextParams);
    }
  };

  const clampPage = (nextExistingCount: number, nextLinkedCount: number) => {
    const lastPage = Math.max(1, Math.ceil((nextExistingCount + nextLinkedCount) / params.limit));

    changeParams({ ...params, offset: Math.min(params.offset, (lastPage - 1) * params.limit) }, nextExistingCount);
  };

  const linkPricing = (selectedPricing: Pricing[]) => {
    const linkedIds = new Set(linkedPricing.map(({ id }) => id));
    const nextLinkedPricing = [...linkedPricing, ...selectedPricing.filter(({ id }) => !linkedIds.has(id))];
    const lastPage = Math.ceil((existingCount + nextLinkedPricing.length) / params.limit);

    setLinkedPricing(nextLinkedPricing);
    changeParams({ ...params, offset: (lastPage - 1) * params.limit });
  };

  const deleteSelected = () => {
    const deletedIds = new Set(selectedItems[0] === SELECTED_ALL_ID ? items.map(({ id }) => id) : selectedItems);
    const linkedIds = new Set(linkedPricing.map(({ id }) => id));
    const keys = [...deletedIds].filter((id) => !linkedIds.has(id));
    const nextLinkedPricing = linkedPricing.filter(({ id }) => !deletedIds.has(id));

    setSelectedItems([]);
    setLinkedPricing(nextLinkedPricing);

    if (keys.length) {
      deletePricing(keys, { onSuccess: () => clampPage(existingCount - keys.length, nextLinkedPricing.length) });
    } else {
      clampPage(existingCount, nextLinkedPricing.length);
    }
  };

  const confirmDelete = () => {
    dialogs.error({
      actions: [
        {
          label: 'Delete pricing',
          onPress: deleteSelected,
          variant: 'destructive',
        },
        {
          label: t('translation:default.buttons.cancel'),
        },
      ],
      content: 'Are you sure you want to delete the selected pricing from the contract?',
      title: 'Delete pricing',
    });
  };

  useImperativeHandle(pricingRef, () => ({
    linkedPricing,
    reset: () => {
      const firstPageParams = { ...params, offset: 0 };

      setLinkedPricing([]);
      setSelectedItems([]);
      setParams(firstPageParams);
      setRequestParams(firstPageParams);
    },
  }));

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <Text size="sm" weight="semibold">
          Pricing
        </Text>
        <div className={styles.actions}>
          {totalCount ? (
            <Button
              iconLeft="trash"
              secondary
              outline
              isDisabled={selectedItems.length === 0}
              isLoading={isDeleting}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          ) : null}
          <Button iconLeft="squares-exclude" primary onClick={() => setIsPricingSearchOpen(true)}>
            Link pricing
          </Button>
        </div>
      </div>
      {contractKey && isLoading ? (
        <div className={cx(styles.tableContainer, styles.loader)}>
          <Spinner size="lg" />
        </div>
      ) : totalCount ? (
        <>
          <div className={styles.tableContainer}>
            <DataTable
              data={items}
              columns={columns}
              page={page}
              limit={params.limit}
              totalItems={totalCount}
              selectedItems={selectedItems}
              onSelectionChange={setSelectedItems}
              columnPinning={{ left: ['pricingKey'] }}
            />
          </div>
          <Pagination
            page={page}
            limit={params.limit}
            totalItems={totalCount}
            onPageChange={(nextPage: number) => changeParams({ ...params, offset: (nextPage - 1) * params.limit })}
            onLimitChange={(limit: number) => changeParams({ ...params, limit, offset: 0 })}
          />
        </>
      ) : (
        <Text className={styles.emptyState} size="sm">
          No contract is linked to Pricing. Click the &apos;Link Pricing&apos; button to link a contract.
        </Text>
      )}
      <PricingSearchModal
        isOpen={isPricingSearchOpen}
        setIsOpen={setIsPricingSearchOpen}
        onSelectionChange={linkPricing}
      />
    </section>
  );
};
